import { useState, useCallback } from 'react';
import { IPublicClientApplication } from '@azure/msal-browser';
import { UserCreatedPrompt } from './types';
import { savePrompt, getSavedPrompts, updateSavedPrompt, deleteSavedPrompt } from '../../utils/savedPrompts.service';
export function useUserCreatedPrompts() {
  const [userPrompts, setUserPrompts] = useState<UserCreatedPrompt[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchUserPrompts = useCallback(async (msalInstance: IPublicClientApplication) => {
    setIsLoading(true);
    setError(null);
    try {
      const prompts = await getSavedPrompts(msalInstance);
      setUserPrompts(prompts);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch prompts');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const saveUserPrompt = useCallback(async (
    msalInstance: IPublicClientApplication,
    prompt: Omit<UserCreatedPrompt, 'id' | 'createdAt'>
  ) => {
    setError(null);
    try {
      const saved = await savePrompt(msalInstance, prompt);
      setUserPrompts((prev) => [...prev, saved]);
      return saved;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save prompt');
      throw err;
    }
  }, []);

  const updateUserPrompt = useCallback(async (
    msalInstance: IPublicClientApplication,
    promptId: string,
    updates: Partial<Omit<UserCreatedPrompt, 'id' | 'createdAt'>>
  ) => {
    setError(null);
    try {
      await updateSavedPrompt(msalInstance, promptId, updates);
      // API returns { prompt_id, status } only — merge updates into local state
      setUserPrompts((prev) => prev.map((p) => p.id === promptId ? { ...p, ...updates } : p));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update prompt');
      throw err;
    }
  }, []);

  const deleteUserPrompt = useCallback(async (
    msalInstance: IPublicClientApplication,
    promptId: string
  ) => {
    setError(null);
    try {
      await deleteSavedPrompt(msalInstance, promptId);
      setUserPrompts((prev) => prev.filter((p) => p.id !== promptId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete prompt');
      throw err;
    }
  }, []);

  return {
    userPrompts,
    isLoading,
    error,
    fetchUserPrompts,
    saveUserPrompt,
    updateUserPrompt,
    deleteUserPrompt,
  };
}
